// src/admin/screens/kyc/KycReviewScreen.tsx
// KYC reviewer's workbench. Hero header (avatar + name + status + meta) with
// the three decision actions on the right; below: rejection notice if any,
// PhotoPanel (signed-URL photos), OCR compare table.
//
// The decision actions are hidden when the submission is in a terminal state
// (verified/rejected) so the reviewer can't fire a second write on a row
// that's already been actioned; the server-side trigger enforces the same
// invariant defensively.
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, X, AlertCircle } from 'lucide-react';
import { useKycSubmission } from '@admin/hooks/useKycSubmission';
import { useRole } from '@admin/auth/useRole';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import StatusChip from '@admin/components/StatusChip';
import Skeleton from '@admin/components/Skeleton';
import Avatar from '@admin/components/Avatar';
import { KYC_STATUS_LABEL, KYC_STATUS_TONE } from '@admin/utils/kycStatus';
import { formatCatDateTime } from '@admin/utils/dates';
import { formatZambiaPhone } from '@admin/utils/phone';
import type { Json } from '@/lib/database.types';
import PhotoPanel from './PhotoPanel';
import OcrCompare from './OcrCompare';
import ApproveKycModal from './modals/ApproveKycModal';
import RejectKycModal from './modals/RejectKycModal';
import RequestResubmitModal from './modals/RequestResubmitModal';
import styles from './KycReviewScreen.module.css';

type ModalKind = null | 'approve' | 'reject' | 'resubmit';

function ocrAsObject(v: Json | null): Record<string, unknown> | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

export default function KycReviewScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useRole();
  const { data: kyc, isLoading, error, refetch } = useKycSubmission(id);
  const [modal, setModal] = useState<ModalKind>(null);

  if (isLoading) {
    return (
      <div className={styles.page}>
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} h={20} />)}
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.page}>
        <p style={{ color: 'var(--c-danger)' }}>{(error as Error).message}</p>
      </div>
    );
  }
  if (!kyc || !id) {
    return <div className={styles.page}><p>Submission not found.</p></div>;
  }

  const pending = kyc.status === 'pending' || kyc.status === 'manual_review';
  const canAct = can('kyc.review') && pending;
  const profileName =
    kyc.profile?.display_name ?? kyc.profile?.full_name ?? 'Unknown borrower';
  const closeModal = (refresh: boolean) => {
    setModal(null);
    if (refresh) void refetch();
  };

  return (
    <div className={styles.page}>
      <button type="button" className={styles.back} onClick={() => navigate('/kyc')}>
        <ChevronLeft size={14} /> KYC queue
      </button>

      <div className={styles.heroRow}>
        <div className={styles.heroLeft}>
          <Avatar name={profileName} size={56} />
          <div className={styles.heroText}>
            <div className={styles.heroChips}>
              <span className={styles.heroId}>{id.slice(0, 8)}</span>
              <StatusChip label={KYC_STATUS_LABEL[kyc.status]} tone={KYC_STATUS_TONE[kyc.status]} />
            </div>
            <h1 className={styles.heroTitle}>{profileName}</h1>
            <p className={styles.heroSub}>
              Submitted {formatCatDateTime(kyc.submitted_at)} · {formatZambiaPhone(kyc.profile?.phone)} · NRC {kyc.nrc_number ?? '—'}
            </p>
          </div>
        </div>
        {canAct && (
          <div className={styles.heroActions}>
            <Button variant="danger" leftIcon={<X size={14} />} onClick={() => setModal('reject')}>Reject</Button>
            <Button variant="outline" leftIcon={<AlertCircle size={14} />} onClick={() => setModal('resubmit')}>Request resubmission</Button>
            <Button variant="success" leftIcon={<Check size={14} />} onClick={() => setModal('approve')}>Approve</Button>
          </div>
        )}
      </div>

      {kyc.status === 'rejected' && kyc.review_notes && (
        <Card title="Rejection reason" accent>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--c-text-sec)', lineHeight: 1.55 }}>
            {kyc.review_notes}
          </p>
          <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--c-text-muted)' }}>
            Reviewed {kyc.reviewed_at ? formatCatDateTime(kyc.reviewed_at) : '—'}
            {kyc.reviewer?.display_name ? ` by ${kyc.reviewer.display_name}` : ''}
          </p>
        </Card>
      )}

      {kyc.status === 'manual_review' && kyc.review_notes && (
        <Card title="Resubmission message sent to borrower">
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--c-text-sec)', lineHeight: 1.55 }}>
            {kyc.review_notes}
          </p>
        </Card>
      )}

      <PhotoPanel
        submissionId={id}
        front={kyc.nrc_front_path}
        back={kyc.nrc_back_path}
        selfie={kyc.selfie_path}
      />

      {kyc.profile && (
        <OcrCompare
          profile={{
            display_name: kyc.profile.display_name,
            full_name: kyc.profile.full_name,
            date_of_birth: kyc.profile.date_of_birth,
            gender: kyc.profile.gender,
            nrc_number: kyc.profile.nrc_number,
          }}
          ocr={ocrAsObject(kyc.ocr_result_jsonb as Json | null)}
        />
      )}

      {modal === 'approve' && (
        <ApproveKycModal
          kyc={{ id, user_id: kyc.user_id, nrc_number: kyc.nrc_number }}
          onClose={closeModal}
        />
      )}
      {modal === 'reject' && (
        <RejectKycModal kyc={{ id, user_id: kyc.user_id }} onClose={closeModal} />
      )}
      {modal === 'resubmit' && (
        <RequestResubmitModal kyc={{ id, user_id: kyc.user_id }} onClose={closeModal} />
      )}
    </div>
  );
}
